import React from "react";
import {
  Box, Stack, Typography, Dialog, IconButton, Collapse, Tooltip,
} from "@mui/material";
import { ReactComponent as PlayIcon } from "../../vendor/_new/icon/play.svg";
import { Lightbulb, ChevronUp } from "lucide-react";
import { Close as CloseIcon } from "@mui/icons-material";

import LayerdIcon from "../../components/common/LayerdIcon";
import colors from "../../utils/colors";
import { useHowToWork } from "./HowToWorkContext";

// Video hosting is external (arbitrary iframe embeds — Vimeo,
// Cloudflare Stream, self-hosted, whatever the slide specifies). No
// YouTube-specific helpers here: `slide.videoUrl` is passed straight
// to the iframe `src` and `slide.thumbnailUrl` is the panel image if
// the slide chooses to provide one. Both are optional; the render
// falls back to opening the docs link when no video URL is set so
// the button stays useful until per-slide videos are wired.

/** Compact control in the fixed top bar — beside global search (LDCP-99). */
export function HowToWorkHeaderTrigger() {
  const { hideFlag, toggle } = useHowToWork();
  const label = hideFlag ? "How it works" : "Hide panel";
  const panelOpen = !hideFlag;

  return (
    <Tooltip title={label} arrow>
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", lineHeight: 0, flexShrink: 0 }}
      >
        <Box
          component="button"
          type="button"
          onClick={toggle}
          aria-label={label}
          aria-expanded={panelOpen}
          sx={{
          width: 32,
          height: 32,
          p: 0,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          backgroundColor: panelOpen ? colors.primary[100] : "transparent",
          "&:hover": {
            backgroundColor: colors.primary[100],
            "& svg": {
              color: colors.primary[300],
            },
          },
          "& svg": {
            color: panelOpen ? colors.primary[300] : colors.neutral[600],
          },
        }}
      >
          <LayerdIcon
            icon={panelOpen ? ChevronUp : Lightbulb}
            size={20}
            color="inherit"
          />
        </Box>
      </Box>
    </Tooltip>
  );
}

/** Expandable panel between the fixed header and page scroll area. */
export function HowToWorkPanel() {
  const { hideFlag, slide, setVideoModalOpen } = useHowToWork();
  return (
    <Collapse in={!hideFlag}>
      <Box sx={{ px: 6, pt: 1, pb: 2, backgroundColor: colors.neutral[50], borderBottom: `1px solid ${colors.neutral[200]}` }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={6}
          sx={{
            width: "100%",
            px: 4,
            py: 4,
            backgroundColor: "#fff",
            border: `solid 1px ${colors.neutral[200]}`,
            borderRadius: "8px",
          }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            onClick={() => {
              // Preferred: open the iframe modal with the slide's video.
              // Fallback: open the docs page in a new tab so the click
              // still does something useful until per-slide videoUrls
              // are wired up. The thumbnail area is always clickable.
              if (slide?.videoUrl) {
                setVideoModalOpen(true);
              } else if (slide?.learnMoreUrl) {
                window.open(slide.learnMoreUrl, "_blank", "noopener,noreferrer");
              }
            }}
            sx={{
              minWidth: { xs: 200, md: 280, lg: 320 },
              height: { xs: 120, md: 150, lg: 172 },
              borderRadius: "8px",
              backgroundColor: colors.primary.alpha,
              backgroundImage: slide?.thumbnailUrl ? `url(${slide.thumbnailUrl})` : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
              cursor: "pointer",
              flexShrink: 0,
              position: "relative",
              "&:hover": { transform: "scale(1.02)" },
              "&::before": slide?.thumbnailUrl ? {
                content: '""',
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                borderRadius: "8px",
              } : {},
            }}
          >
            <PlayIcon style={{ width: 48, height: 48, color: "#fff", position: "relative", zIndex: 1 }} />
          </Stack>
          <Stack sx={{ maxWidth: 846, minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, lineHeight: "25px", fontWeight: 500, color: colors.neutral[1000], pb: 1 }}>
              {slide?.title || "Learn more"}
            </Typography>
            {/* Paragraphs come from the per-route slide (see
                src/config/learnMoreSlides.js). Each entry in the array
                becomes its own <Typography> block with a small margin
                between paragraphs. */}
            {(Array.isArray(slide?.paragraphs) ? slide.paragraphs : []).map((para, i) => (
              <Typography
                key={i}
                sx={{
                  fontSize: 14,
                  lineHeight: "25px",
                  color: colors.neutral[800],
                  mt: i === 0 ? 0 : 1,
                }}
              >
                {para}
              </Typography>
            ))}
            <Stack direction="row" alignItems="center" spacing={4} sx={{ mt: 2 }}>
              {slide?.learnMoreUrl && (
                <Typography
                  component="a"
                  href={slide.learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: 14, lineHeight: "25px", color: colors.primary[300], textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  Learn more
                </Typography>
              )}
              {(slide?.videoUrl || slide?.learnMoreUrl) && (
                <Typography
                  onClick={() => {
                    if (slide?.videoUrl) {
                      setVideoModalOpen(true);
                    } else if (slide?.learnMoreUrl) {
                      window.open(slide.learnMoreUrl, "_blank", "noopener,noreferrer");
                    }
                  }}
                  sx={{ fontSize: 14, lineHeight: "25px", color: colors.primary[300], cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  Watch Academy Video
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Collapse>
  );
}

export function HowToWorkVideoModal() {
  const { videoModalOpen, setVideoModalOpen, slide } = useHowToWork();
  return (
    <Dialog
      open={videoModalOpen}
      onClose={() => setVideoModalOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: "transparent", boxShadow: "none", overflow: "visible" },
      }}
    >
      <Box sx={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
        <IconButton
          onClick={() => setVideoModalOpen(false)}
          sx={{
            position: "absolute", top: -40, right: 0, color: "#fff", zIndex: 1,
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        {videoModalOpen && slide?.videoUrl && (
          <iframe
            src={slide.videoUrl}
            title={slide?.videoTitle || slide?.title || "Video"}
            style={{
              position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
              border: "none", borderRadius: "8px",
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </Box>
    </Dialog>
  );
}

/** @deprecated Use HowToWorkProvider + HowToWorkHeaderTrigger + HowToWorkPanel */
const HowToWork = () => null;

export default HowToWork;
